import { FC, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Breadcrumb, Spin, Button, Space, Row } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Content, Header } from '@/components';
import { StyledTitle, PlanSummaryTable, AllocatedBudgetValue, GrossProfitValue } from './MediaPlanDetails.styles';
import { useMediaPlanDetails } from './hooks/useMediaPlanDetails';
import { Actions, ChannelForm, ChannelList, RemovedLineItems } from './components';
import { useModal } from '@/hooks/useModal';
import { DATE_FORMAT, NAMESPACE } from '@/constants';
import { formatMoney } from '@/constants/currency';
import { ChannelListItem } from '@/models';
import { formatNumber, pipedriveDealUrl } from '@/utils';
import { useGetChannelsBudgetQuery, useGetChannelListQuery } from '@/api';
import {
  getMediaPlanClientName,
  getMediaPlanDealId,
  getMediaPlanManagerName,
  getMediaPlanTotalBudget,
  getMediaPlanTotalHardCosts,
  getMediaPlanAllocationPercent,
  resolveMediaPlanCurrency
} from '@/api/mediaPlanMappers';
import { getMediaPlanTotalQuantity } from './utils/deliveryMetrics';

const { Item } = PlanSummaryTable;

const getDateFormat = (date?: Date): string => (date ? dayjs(date).format(DATE_FORMAT) : 'N/A');
const getNumberFormat = (value?: number): string => (value ? formatNumber(value, 0) : 'N/A');
const getCurrencyFormat = (value?: number, currency?: string | null): string =>
  value != null && Number.isFinite(value) ? formatMoney(value, currency) : 'N/A';
const getPercentFormat = (value?: number): string =>
  value == null || Number.isNaN(value)
    ? 'N/A'
    : `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}%`;

const MediaPlanDetails: FC = () => {
  const [channel, setChannel] = useState<ChannelListItem>();
  const { isOpen, close, open } = useModal();
  const { t } = useTranslation();
  const { id, breadCrumbItems, isMediaPlanLoading, navigateToMediaPlanList, mediaPlan } =
    useMediaPlanDetails();
  const { data: budget } = useGetChannelsBudgetQuery({ mediaPlanId: id });
  const { data: channelsForCurrency } = useGetChannelListQuery(
    { page: 1, limit: 100, order: -1, mediaPlan: [id] },
    { skip: !id }
  );

  const planCurrency = useMemo(
    () =>
      resolveMediaPlanCurrency(
        mediaPlan,
        channelsForCurrency?.data?.map((channel) => channel.currency)
      ),
    [mediaPlan, channelsForCurrency?.data]
  );
  const planTotalBudget = getMediaPlanTotalBudget(mediaPlan);
  const planTotalHardCosts = useMemo(
    () => getMediaPlanTotalHardCosts(mediaPlan, channelsForCurrency?.data),
    [mediaPlan, channelsForCurrency?.data]
  );
  const planTotalQuantity = useMemo(
    () => getMediaPlanTotalQuantity(channelsForCurrency?.data, mediaPlan?.impressions),
    [channelsForCurrency?.data, mediaPlan?.impressions]
  );

  /* Mirrors the API's test: a plan that has raised an IO, or is booked through
     Prisma, may have lines on the booking sheet. */
  const isBooked = Boolean(
    mediaPlan?.ioDocuments?.length || mediaPlan?.ioDocument?.id || mediaPlan?.prismaStatus
  );

  const dealId = getMediaPlanDealId(mediaPlan);
  const dealUrl = pipedriveDealUrl(dealId);

  const handleFormOpen = (data?: ChannelListItem) => {
    setChannel(data);
    open();
  };
  const allocatedBudget = budget ?? 0;
  const budgetPercent = getMediaPlanAllocationPercent(allocatedBudget, planTotalBudget);
  const isUnderAllocated = planTotalBudget != null && allocatedBudget < planTotalBudget - 0.01;
  const isOverAllocated = planTotalBudget != null && allocatedBudget > planTotalBudget + 0.01;

  return (
    <>
      <Header>
        <Spin spinning={isMediaPlanLoading}>
          <Space size={12} direction="vertical">
            <Breadcrumb items={breadCrumbItems} />
            <Row justify="space-between">
              <Space size={16}>
                <Button
                  type="text"
                  onClick={navigateToMediaPlanList}
                  icon={<ArrowLeftOutlined size={13} />}
                />
                <StyledTitle
                  ellipsis={{ tooltip: typeof mediaPlan?.campaign === 'object' ? mediaPlan.campaign.name : undefined }}
                  level={3}
                >
                  {typeof mediaPlan?.campaign === 'object' ? mediaPlan.campaign.name : 'N/A'}
                </StyledTitle>
              </Space>
              <Actions id={id} mediaPlan={mediaPlan} />
            </Row>
            <PlanSummaryTable bordered size="small" column={4}>
              <Item label={t('client')}>{getMediaPlanClientName(mediaPlan) || 'N/A'}</Item>
              <Item label="Deal ID">
                {/* Opens the PipeDrive deal, in a new tab so the plan stays open. */}
                {dealUrl ? (
                  <a href={dealUrl} target="_blank" rel="noreferrer noopener">
                    {dealId}
                  </a>
                ) : (
                  dealId ?? 'N/A'
                )}
              </Item>
              <Item label={t('campaignDates')}>
                {getDateFormat(mediaPlan?.startDate)} – {getDateFormat(mediaPlan?.endDate)}
              </Item>
              <Item label={t('responsibleAM')}>{getMediaPlanManagerName(mediaPlan) || 'N/A'}</Item>
              <Item label={t('scheduleDate')}>{getDateFormat(mediaPlan?.scheduleDate)}</Item>
              <Item label={t('currency', { ns: NAMESPACE.MEDIA_PLAN_PAGE })}>{planCurrency}</Item>
              <Item label="Buying Point">
                {mediaPlan?.buyingPoint?.trim() || getMediaPlanClientName(mediaPlan) || 'N/A'}
              </Item>
              <Item label={t('totalBudget')}>{getCurrencyFormat(planTotalBudget, planCurrency)}</Item>
              <Item label={t('totalHardCosts', { ns: NAMESPACE.MEDIA_PLAN_PAGE })}>
                {getCurrencyFormat(planTotalHardCosts, planCurrency)}
              </Item>
              <Item label={t('allocatedBudget')}>
                <AllocatedBudgetValue $isUnderAllocated={isUnderAllocated} $isOverAllocated={isOverAllocated}>
                  {getCurrencyFormat(allocatedBudget, planCurrency)} ({budgetPercent.toFixed(1)}%)
                </AllocatedBudgetValue>
              </Item>
              <Item label={t('totalMargin')}>
                <GrossProfitValue>{getPercentFormat(mediaPlan?.marginPercent)}</GrossProfitValue>
              </Item>
              <Item label={t('quantity', { ns: NAMESPACE.MEDIA_PLAN_PAGE })}>
                {getNumberFormat(planTotalQuantity)}
              </Item>
            </PlanSummaryTable>
          </Space>
        </Spin>
      </Header>
      <Content>
        <ChannelForm
          channel={channel}
          isOpen={isOpen}
          close={close}
          mediaPlanId={id}
          totalBudget={planTotalBudget}
          planCurrency={planCurrency}
          planStartDate={mediaPlan?.startDate}
          planEndDate={mediaPlan?.endDate}
        />
        <ChannelList
          openChannelForm={handleFormOpen}
          mediaPlanId={id}
          planCurrency={planCurrency}
          agencyDiscount={mediaPlan?.agencyDiscount}
          isBooked={isBooked}
        />
        <RemovedLineItems items={mediaPlan?.removedLineItems} planCurrency={planCurrency} />
      </Content>
    </>
  );
};

export default MediaPlanDetails;
